import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, getDocs, query, orderBy, serverTimestamp, 
  updateDoc, doc, deleteDoc, increment, where, getDoc
} from 'firebase/firestore';

const BOARD_CATEGORIES = [
  { id: 'notice', name: '📢 공지사항', color: '#ef4444', bgColor: '#fef2f2' },
  { id: 'guild', name: '🏰 길드 모집', color: '#8b5cf6', bgColor: '#faf5ff' },
  { id: 'lesson', name: '🎓 레슨 모집', color: '#0ea5e9', bgColor: '#f0f9ff' },
  { id: 'together', name: '⚡ 같이 운동해요', color: '#10b981', bgColor: '#f0fdf4' },
  { id: 'free', name: '💬 자유게시판', color: '#f59e0b', bgColor: '#fffbeb' }
];

function CommunityBoard() {
  const [localUser, setLocalUser] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('notice');
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'notice' });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  // 사용자 정보 로드
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setLocalUser(storedUser);
    setUser(storedUser);
  }, []);

  // 게시글 목록 가져오기
  const fetchPosts = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'community_posts'),
        where('category', '==', selectedCategory),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    } catch (error) {
      console.error('게시글 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 댓글 가져오기
  const fetchComments = async (postId) => {
    try {
      const q = query(
        collection(db, 'community_comments'),
        where('postId', '==', postId),
        orderBy('createdAt', 'asc')
      );
      const snapshot = await getDocs(q);
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
    } catch (error) {
      console.error('댓글 로드 실패:', error);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedPost) {
      fetchComments(selectedPost.id);
    }
  }, [selectedPost]);

  // 게시글 작성
  const handleCreatePost = async () => {
    if (!localUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      alert('제목과 내용을 모두 입력해주세요.');
      return;
    }

    // 공지사항 카테고리인 경우 관리자만 작성 가능
    if (newPost.category === 'notice' && user?.role !== 'admin') {
      alert('관리자만 공지사항을 작성할 수 있습니다.');
      return;
    }

    try {
      await addDoc(collection(db, 'community_posts'), {
        ...newPost,
        authorId: localUser.id,
        authorName: localUser.name || localUser.nickname || '알 수 없음',
        createdAt: serverTimestamp(),
        likes: 0,
        dislikes: 0,
        likedBy: [],
        dislikedBy: [],
        commentCount: 0
      });
      
      setNewPost({ title: '', content: '', category: selectedCategory });
      setShowCreatePost(false);
      fetchPosts();
      alert('게시글이 작성되었습니다!');
    } catch (error) {
      console.error('게시글 작성 실패:', error);
      alert('게시글 작성에 실패했습니다.');
    }
  };

  // 댓글 작성
  const handleCreateComment = async () => {
    if (!localUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!newComment.trim()) return;

    try {
      await addDoc(collection(db, 'community_comments'), {
        postId: selectedPost.id,
        authorId: localUser.id,
        authorName: localUser.name || localUser.nickname || '알 수 없음',
        content: newComment,
        createdAt: serverTimestamp(),
        likes: 0,
        dislikes: 0
      });

      // 게시글의 댓글 수 증가
      await updateDoc(doc(db, 'community_posts', selectedPost.id), {
        commentCount: increment(1)
      });

      // 게시글 작성자에게 알림 (본인 댓글 제외)
      if (selectedPost.authorId !== localUser.id) {
        await addDoc(collection(db, 'notifications'), {
          from: localUser.id,
          to: selectedPost.authorId,
          type: 'comment',
          postId: selectedPost.id,
          postTitle: selectedPost.title,
          createdAt: serverTimestamp(),
          read: false
        });
      }

      setNewComment('');
      fetchComments(selectedPost.id);
      fetchPosts(); // 댓글 수 업데이트를 위해
    } catch (error) {
      console.error('댓글 작성 실패:', error);
    }
  };

  // 추천/비추천
  const handleLike = async (postId, isLike) => {
    if (!localUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      const postRef = doc(db, 'community_posts', postId);
      const postDoc = await getDoc(postRef);
      const postData = postDoc.data();

      const likedBy = postData.likedBy || [];
      const dislikedBy = postData.dislikedBy || [];

      if (isLike) {
        if (likedBy.includes(localUser.id)) {
          // 추천 취소
          await updateDoc(postRef, {
            likes: increment(-1),
            likedBy: likedBy.filter(id => id !== localUser.id)
          });
        } else {
          // 추천 (기존 비추천이 있으면 제거)
          const updates = {
            likes: increment(1),
            likedBy: [...likedBy, localUser.id]
          };
          if (dislikedBy.includes(localUser.id)) {
            updates.dislikes = increment(-1);
            updates.dislikedBy = dislikedBy.filter(id => id !== localUser.id);
          }
          await updateDoc(postRef, updates);
        }
      } else {
        if (dislikedBy.includes(localUser.id)) {
          // 비추천 취소
          await updateDoc(postRef, {
            dislikes: increment(-1),
            dislikedBy: dislikedBy.filter(id => id !== localUser.id)
          });
        } else {
          // 비추천 (기존 추천이 있으면 제거)
          const updates = {
            dislikes: increment(1),
            dislikedBy: [...dislikedBy, localUser.id]
          };
          if (likedBy.includes(localUser.id)) {
            updates.likes = increment(-1);
            updates.likedBy = likedBy.filter(id => id !== localUser.id);
          }
          await updateDoc(postRef, updates);
        }
      }
      
      fetchPosts();
    } catch (error) {
      console.error('추천/비추천 처리 실패:', error);
    }
  };

  // 게시글/댓글 삭제
  const handleDelete = async (type, id, postId = null) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      if (type === 'post') {
        await deleteDoc(doc(db, 'community_posts', id));
        // 해당 게시글의 모든 댓글도 삭제
        const commentsQuery = query(
          collection(db, 'community_comments'),
          where('postId', '==', id)
        );
        const commentsSnapshot = await getDocs(commentsQuery);
        commentsSnapshot.docs.forEach(async (commentDoc) => {
          await deleteDoc(doc(db, 'community_comments', commentDoc.id));
        });
        
        setSelectedPost(null);
        fetchPosts();
      } else {
        await deleteDoc(doc(db, 'community_comments', id));
        // 게시글의 댓글 수 감소
        await updateDoc(doc(db, 'community_posts', postId), {
          commentCount: increment(-1)
        });
        fetchComments(postId);
        fetchPosts();
      }
      
      alert('삭제되었습니다.');
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('ko-KR') + ' ' + date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // 게시글 상세보기
  if (selectedPost) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          padding: '12px 0',
          borderBottom: '2px solid #f1f5f9'
        }}>
          <button
            onClick={() => setSelectedPost(null)}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              color: '#64748b'
            }}
          >
            ← 목록으로
          </button>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
            {BOARD_CATEGORIES.find(cat => cat.id === selectedPost.category)?.name}
          </span>
        </div>

        {/* 게시글 내용 */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: 20
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 12
          }}>
            <h2 style={{
              fontSize: 20,
              fontWeight: 700,
              margin: 0,
              color: '#1e293b',
              lineHeight: 1.4
            }}>
              {selectedPost.title}
            </h2>
            {selectedPost.authorId === localUser.id && (
              <button
                onClick={() => handleDelete('post', selectedPost.id)}
                style={{
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                삭제
              </button>
            )}
          </div>
          
          <div style={{
            display: 'flex',
            gap: 12,
            fontSize: 14,
            color: '#64748b',
            marginBottom: 16
          }}>
            <span>작성자: {selectedPost.authorName}</span>
            <span>•</span>
            <span>{formatDate(selectedPost.createdAt)}</span>
          </div>

          <div style={{
            fontSize: 16,
            lineHeight: 1.6,
            color: '#334155',
            marginBottom: 20,
            whiteSpace: 'pre-wrap'
          }}>
            {selectedPost.content}
          </div>

          {/* 추천/비추천 버튼 */}
          <div style={{
            display: 'flex',
            gap: 8,
            paddingTop: 16,
            borderTop: '1px solid #f1f5f9'
          }}>
            <button
              onClick={() => handleLike(selectedPost.id, true)}
              style={{
                background: selectedPost.likedBy?.includes(localUser.id) ? '#10b981' : '#f8fafc',
                color: selectedPost.likedBy?.includes(localUser.id) ? '#fff' : '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              👍 {selectedPost.likes || 0}
            </button>
            <button
              onClick={() => handleLike(selectedPost.id, false)}
              style={{
                background: selectedPost.dislikedBy?.includes(localUser.id) ? '#ef4444' : '#f8fafc',
                color: selectedPost.dislikedBy?.includes(localUser.id) ? '#fff' : '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              👎 {selectedPost.dislikes || 0}
            </button>
          </div>
        </div>

        {/* 댓글 섹션 */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 16,
            color: '#1e293b'
          }}>
            💬 댓글 ({comments.length})
          </h3>

          {/* 댓글 작성 */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginBottom: 20,
            padding: 16,
            background: '#f8fafc',
            borderRadius: 12
          }}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 입력하세요..."
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateComment()}
            />
            <button
              onClick={handleCreateComment}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              작성
            </button>
          </div>

          {/* 댓글 목록 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {comments.map(comment => (
              <div key={comment.id} style={{
                padding: 16,
                background: '#fafafa',
                borderRadius: 12,
                border: '1px solid #f1f5f9'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8
                }}>
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    fontSize: 12,
                    color: '#64748b'
                  }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      {comment.authorName}
                    </span>
                    <span>{formatDate(comment.createdAt)}</span>
                  </div>
                  {comment.authorId === localUser.id && (
                    <button
                      onClick={() => handleDelete('comment', comment.id, selectedPost.id)}
                      style={{
                        background: 'none',
                        color: '#dc2626',
                        border: 'none',
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      삭제
                    </button>
                  )}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#374151',
                  lineHeight: 1.5
                }}>
                  {comment.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 게시글 작성 폼
  if (showCreatePost) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          padding: '12px 0',
          borderBottom: '2px solid #f1f5f9'
        }}>
          <button
            onClick={() => setShowCreatePost(false)}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              color: '#64748b'
            }}
          >
            ← 취소
          </button>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
            새 게시글 작성
          </span>
        </div>

        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8
            }}>
              카테고리
            </label>
            <select
              value={newPost.category}
              onChange={(e) => setNewPost({...newPost, category: e.target.value})}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none'
              }}
            >
              {BOARD_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8
            }}>
              제목
            </label>
            <input
              type="text"
              value={newPost.title}
              onChange={(e) => setNewPost({...newPost, title: e.target.value})}
              placeholder="제목을 입력하세요"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none'
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8
            }}>
              내용
            </label>
            <textarea
              value={newPost.content}
              onChange={(e) => setNewPost({...newPost, content: e.target.value})}
              placeholder="내용을 입력하세요"
              rows={8}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCreatePost(false)}
              style={{
                background: '#f8fafc',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              취소
            </button>
            <button
              onClick={handleCreatePost}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              작성하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 메인 게시판 목록
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* 카테고리 탭 */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        flexWrap: 'wrap'
      }}>
        {BOARD_CATEGORIES.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            style={{
              background: selectedCategory === category.id ? category.color : category.bgColor,
              color: selectedCategory === category.id ? '#fff' : category.color,
              border: `2px solid ${category.color}`,
              borderRadius: 12,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* 글쓰기 버튼 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 16
      }}>
        {user?.role === 'admin' && (
          <button
            onClick={() => {
              setNewPost({...newPost, category: selectedCategory});
              setShowCreatePost(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            ✏️ 글쓰기
          </button>
        )}
      </div>
      
      {/* 글쓰기 권한 안내 */}
      {user && user.role !== 'admin' && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          fontSize: 14,
          color: '#92400e'
        }}>
          ⚠️ 이 게시판은 관리자만 글을 작성할 수 있습니다.
        </div>
      )}

      {/* 게시글 목록 */}
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: '#64748b'
          }}>
            로딩 중...
          </div>
        ) : posts.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: '#64748b'
          }}>
            {BOARD_CATEGORIES.find(cat => cat.id === selectedCategory)?.name}에 첫 번째 게시글을 작성해보세요!
          </div>
        ) : (
          posts.map((post, index) => (
            <div
              key={post.id}
              onClick={() => setSelectedPost(post)}
              style={{
                padding: 20,
                borderBottom: index < posts.length - 1 ? '1px solid #f1f5f9' : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 8
              }}>
                <h3 style={{
                  fontSize: 16,
                  fontWeight: 600,
                  margin: 0,
                  color: '#1e293b',
                  lineHeight: 1.4
                }}>
                  {post.title}
                </h3>
                <div style={{
                  display: 'flex',
                  gap: 12,
                  fontSize: 12,
                  color: '#64748b'
                }}>
                  <span>👍 {post.likes || 0}</span>
                  <span>💬 {post.commentCount || 0}</span>
                </div>
              </div>
              
              <div style={{
                fontSize: 14,
                color: '#64748b',
                marginBottom: 8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {post.content}
              </div>
              
              <div style={{
                display: 'flex',
                gap: 12,
                fontSize: 12,
                color: '#94a3b8'
              }}>
                <span>{post.authorName}</span>
                <span>•</span>
                <span>{formatDate(post.createdAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CommunityBoard;